import type { CasesLocale } from '../types'

export const zhCases: CasesLocale = {
  hero: {
    tag: '案例 · Ontology 真正落地的地方',
    title: '16 个 ontology + Agent 已经跑起来的场景',
    subtitle: '真实客户 · 真实前后对比 · 覆盖制造、能源、政府、金融、消费品、研发',
    leadParagraph:
      '下面每一个案例都来自真实项目。痛点、做法、量化成果都按公开内容整理。可以从上往下浏览,也可以直接跳到匹配你当前工作的行业 / 职能。',
  },
  filters: {
    industryLabel: '行业',
    functionLabel: '职能',
    resultsCountTpl: '显示 {filtered} / {total} 个案例',
    clearLabel: '清除筛选',
    emptyStateTitle: '没有匹配的案例',
    emptyStateBody: '尝试取消一些筛选条件,扩大范围。',
  },
  sectionLabels: {
    painPoints: '痛点 · 引入前',
    solution: '方案 · 引入后',
    outcomes: '量化成果',
    paradigms: '范式',
    customer: '客户',
    metricHeader: '指标',
    beforeHeader: '引入前',
    afterHeader: '引入后',
  },
  cases: [
    {
      id: 'quality-traceability-fast',
      industryTag: '制造业',
      functionTag: '质量追溯',
      paradigmTags: ['Document-driven', 'Question-grounded', 'Change-propagation'],
      customer: '某头部制造企业',
      title: '分钟级质量追溯',
      painPoints: [
        '一次质量异常牵动 5 个部门、几十份记录,查一次 8 小时以上。',
        '召回范围不准——太宽则成本爆,太窄则审计标红。',
        'ERP / MES / WMS / QMS 命名口径不一,跨系统对账靠人工。',
        '追溯只到批次层面,无法穿透到具体产品 / 产线 / 关键零部件。',
        '历史追溯数据躺在静态归档里,从未反哺到预测或风险评分。',
      ],
      solution: [
        '建质量领域 Ontology:把原料 / 成品 / 工单 / 供应商 / 工序统一成一个语义模型,关系显式表达。',
        '多源图谱:自动抽取 → 实体融合 → 关系入图,把 ERP / MES / WMS / QMS 拼成统一图谱;链路断点用流转模式自动修补。',
        '双向追溯:正向(原料 → 工序 → 成品 → 客户)和反向(客诉 → 成品 → 工单 → 原料 → 供应商)——任意节点都能作为入口。',
        '自然语言追溯 Agent:"帮我查上周用了 3 号原料批次的所有成品,并标出不合格风险最高的工序"——质检不用找 IT。',
      ],
      metrics: [
        { label: '全链路追溯周期', before: '3-7 天', after: '几分钟' },
        { label: '跨部门确认次数', before: '多次拉群/电话', after: '减少 70%' },
        { label: '根因定位准确率', before: '低,靠经验', after: '提升 30%+' },
        { label: '合规审查通过率', before: '存不一致风险', after: '稳定 100%' },
        { label: '新员工上手时间', before: '2-3 周', after: '几天' },
      ],
      insight:
        '追溯从"按时间线串"变成"一键图谱遍历",从"查询"变成"推理"。',
    },
    {
      id: 'manufacturing-customer-complaint',
      industryTag: '制造业',
      functionTag: '客诉 · 设计迭代',
      paradigmTags: ['Multi-agent', 'Document-driven', 'Goal-driven'],
      customer: '某制造企业(多部门协作)',
      title: '多部门客诉全链路追溯 + 设计反哺',
      painPoints: [
        '一次客诉,客户经理 / 研发经理 / 成品经理 / 供应链经理需要分别在 CRM / PLM / MES / ERP 中查询。',
        '全链路查询投入约 4 个人天。',
        '低效阻碍研发部门基于客诉模式的设计迭代。',
      ],
      solution: [
        '通过 Domain Scenario Ontology 对全链路节点做语义化建模,关键节点都抽出来。',
        '三个协同 Agent:客诉定位 Agent(从成品批号定位客户)、生产追溯 Agent(查工单、完工单)、供应链追溯 Agent(追采购、原料入库)。',
        '追溯完成后驱动产品迭代 Workflow:需求拆解 + 设计方案生成。',
        '不只查询,还能写回:生成需求规格说明书、设计方案、变更单。',
      ],
      metrics: [
        { label: '协作人数', before: '多部门多人', after: '1 人驱动' },
        { label: '用时', before: '4 个人天', after: '5 分钟' },
        { label: '端到端闭环', before: '仅查询', after: '查询 + 生成 + 写回' },
      ],
      insight:
        '同一份 ontology 既驱动追溯也驱动迭代——客诉成为设计输入,而不是最终归档。',
    },
    {
      id: 'equipment-maintenance',
      industryTag: '制造业',
      functionTag: '预测性维护',
      paradigmTags: ['Event-driven', 'Continuous-audit', 'Explainability', 'What-if'],
      customer: '某大型制造企业(数百台核心生产设备)',
      title: '从被动抢修到主动预判',
      painPoints: [
        '运维数据采集零散,报告靠人工撰写,缺乏可追溯的知识体系。',
        '健康状态不可视,故障机理难量化,根因高度依赖资深专家。',
        '维修方案非标准化、不可复用。',
        '非计划停机频发——持续生产损失 + 运维成本压力。',
      ],
      solution: [
        'Ontology-based Agentic AI 把设备结构、故障机制、运维流程抽象成可计算的知识体系。',
        '三层平台:评估(多维健康评分、关键部件劣化分级、集群协同诊断)→ 预测(RUL、提前预警、故障传导链推演)→ 干预(自动生成维修方案、资源协同调度、动态校验)。',
        '多智能体覆盖报告、诊断、维修端到端;每步自动留痕。',
        '维修完成后数据回流知识库——每跑一轮,系统更聪明。',
      ],
      metrics: [
        { label: '日报 / 周报生成', before: '人工 2-3 小时', after: '5 分钟内' },
        { label: '检测报告生成效率', before: '基线', after: '提升 80%+' },
        { label: '维修效率', before: '基线', after: '提升 40%' },
        { label: '异常排查周期', before: '基线', after: '缩短 70%' },
        { label: '提前预警', before: '基本无', after: '连铸设备振动异常提前 2 周预警(轴承中期磨损)' },
      ],
      insight:
        '维修从"人找方案"变成"方案找人"——标准化、透明化、流程化。',
    },
    {
      id: 'supply-chain-resilience',
      industryTag: '制造 · 物流',
      functionTag: '供应链 · 排产 · 风险',
      paradigmTags: ['Change-propagation', 'Multi-agent', 'Event-driven', 'What-if'],
      customer: '某大型装备制造企业(覆盖原料采购 → 生产 → 订单交付)',
      title: '"可视、可控、可预判"的智能供应链',
      painPoints: [
        'ERP 和 MES 对"产能"定义不一致——每次排产人工对账耗几个小时。',
        '多订单、多约束的排产靠经验,合规风险高。',
        '风险情报散在新闻 / 业务系统 / 人工记录里——无法提前预判。',
        '订单准时率仅 82%,年损失因事件错过超千万元。',
      ],
      solution: [
        '供应链领域 Ontology 把"原料 → 供应商 → 产能 → 订单 → 排产 → 交付"语义统一。',
        '多约束智能排产:把产能上限、交期、环保合规、供应商产能拆成有优先级的约束;大模型生成最优方案,支持可视化拖拽调整。',
        '动态异常响应:监测需求波动、设备故障、原料延期;生成并排序多套恢复方案。',
        '产业链情报中心:全链路 KG(原料 → 供应商 → 生产 → 订单 → 客户 → 政策) + 影响路径可视化 + 风险提前 72 小时预警。',
      ],
      metrics: [
        { label: '订单准时率', before: '82%', after: '98.6%' },
        { label: '数据准确性', before: '基线', after: '99.9%' },
        { label: '排产效率', before: '基线', after: '提升 45%' },
        { label: '合规校验覆盖率', before: '部分', after: '100%' },
        { label: '风险预警时效', before: '滞后', after: '提前 72 小时' },
        { label: '年风险损失', before: '千万元', after: '减少 800 万' },
      ],
      insight:
        '供应链从"事件后反应"变成"事件前预判"——缺的不是数据,是语义对齐这层。',
    },
    {
      id: 'production-management',
      industryTag: '制造业',
      functionTag: '生产排程 · 项目 · 能耗',
      paradigmTags: ['Change-propagation', 'Question-grounded', 'Continuous-audit'],
      customer: '某大型制造企业(系统众多,排产靠人工经验)',
      title: '生产管理 —— 从经验到数据驱动',
      painPoints: [
        '订单 / 物料 / 设备 / 工艺 / 库存数据分布在 ERP / MES / WMS;同一指标含义不一致。',
        '多约束排产复杂度持续上升,规则冲突频繁,人工难以全局权衡。',
        '项目型生产链路长;临时性、探索式分析高度依赖 IT 或数据人员。',
        '能耗管理停在"事后统计"——与设备状态 / 生产节拍 / 环境因素脱节。',
      ],
      solution: [
        '统一生产语义模型:为订单 / 物料 / 设备 / 工艺 / 产能建本体,明确实体、属性、关系、事件。',
        '多约束智能辅助排产:Agent 持续感知实时生产状态;订单调整或设备异常自动触发排产重算与影响评估。',
        '项目全生命周期智能分析:为项目里程碑、生产线工位、生产排期、质检标准建本体;支持自然语言项目查询。',
        '生产 + 能耗联动:自动识别非生产时段异常能耗、设备低效运行,结合气象等外部因素;形成"识别 → 分析 → 调整 → 评估"闭环。',
      ],
      metrics: [
        { label: '跨系统数据协同', before: '人工对账', after: '自动协同' },
        { label: '排产生成时间', before: '基线', after: '大幅缩短' },
        { label: '项目查询响应', before: '按天', after: '小时级' },
        { label: '能耗浪费识别', before: '隐性 / 不可量化', after: '精准识别 · 持续优化' },
      ],
      insight:
        '当系统真的理解生产逻辑,排产从"扯皮"变成"计算"。',
    },
    {
      id: 'rail-inspection-report',
      industryTag: '能源 · 轨交',
      functionTag: '报告 · 合规',
      paradigmTags: ['Document-driven', 'Goal-driven', 'Multi-agent'],
      customer: '能源 / 轨交集团',
      title: '铁路检测报告 —— 70 倍加速',
      painPoints: [
        '一份铁路综合检测报告原本需要 30 人工作 7 天。',
        '人工梳理数据慢、易错,报告质量随团队波动。',
        '同样的数据 + 类似的本体层也支撑安全隐患识别、检修计划、应急预案——但每个都是从头搭。',
      ],
      solution: [
        '本体建模设备 / 缺陷 / 位置 / 检测方法,覆盖完整检测知识图谱。',
        '3 人团队约 1 天把数据梳理进本体;智能体随后 30 分钟出报告。',
        '同一套底座扩到:安全隐患识别、检修计划、应急预案、检修知识陪练、资源匹配。',
      ],
      metrics: [
        { label: '人力投入', before: '30 人 × 7 天', after: '3 人 × 1 天' },
        { label: '出报告', before: '数天', after: '30 分钟(智能体)' },
        { label: '整体效率', before: '基线', after: '~70 倍提升' },
        { label: '准确性', before: '人工', after: '比人工更精准' },
      ],
      insight:
        '一份 ontology,多种报告——一次建模,覆盖整条运营和合规链路。',
    },
    {
      id: 'energy-operations-analysis',
      industryTag: '能源',
      functionTag: '经营分析 · 决策支持',
      paradigmTags: ['Question-grounded', 'Explainability', 'Multi-agent'],
      customer: '某大型能源集团 · 经营管理层',
      title: '自主推理经营分析',
      painPoints: [
        '"人工、联运、电力费用为什么上涨?影响多大?"——这类问题无法直接回答。',
        '6 步人工流程:找数据 → 对齐口径 → 人工分析 → 经验判断 → 写报告 → 做图表 + 多版修改。',
        '端到端:5-7 天,3-6 人。',
        '结论严重受人工经验影响——很难拿到董事会。',
      ],
      solution: [
        '自主规划 Agent:接到自然语言问题后,基于上下文工程自己构建分析计划。',
        '基于业财一体化本体,自动发现并统一数据源(人工/联运/电力/收入/运量)。',
        '在本体定义的经营分析框架(成本结构 / 投入产出 / 效率差异 / 风险点)内调用工具(如 Code Agent)做计算。',
        '输出图表 + 趋势 + 结构 + 同比 + 原因分析,支持连续追问。',
      ],
      metrics: [
        { label: '耗时', before: '5-7 天', after: '30 分钟' },
        { label: '参与人数', before: '3-6 人', after: '1 人驱动' },
        { label: '客观性', before: '受经验影响', after: '基于本体可追溯' },
      ],
      insight:
        '决策支持从"撰写报告的马拉松"变成"对话的速度"——以好奇心的节奏而不是流程的节奏。',
    },
    {
      id: 'finance-investor-disclosure',
      industryTag: '金融',
      functionTag: '监管 · 信披 · 表单填报',
      paradigmTags: ['Document-driven', 'Change-propagation'],
      customer: '金融投资机构(多 LP 场景)',
      title: 'LP 信披自动填报 —— 一个本体,所有模板',
      painPoints: [
        '不同 LP 要求不同的填报模板。',
        '传统做法:为每个模板单独开发智能体——慢、贵、难以迭代。',
      ],
      solution: [
        '用户上传目标模板即可。',
        '系统结合本体平台自动查填数据——不需要描述意图。',
        '模板变化只影响"取数路径",不影响底层数据本体。',
      ],
      metrics: [
        { label: '新增 LP 模板', before: '为模板单独开发智能体', after: '上传即用,自动填报' },
        { label: '信披响应速度与一致性', before: '慢 + 不一致', after: '快 + 一致' },
      ],
      insight:
        '当数据本体稳固,每一份新的报送格式都变成"取数",而不是"重新搭一套"。',
    },
    {
      id: 'led-line-quality',
      industryTag: '制造 · 电子',
      functionTag: '质量 + 任务派发',
      paradigmTags: ['Multi-agent', 'Event-driven', 'Change-propagation'],
      customer: 'LED 生产线',
      title: 'LED 生产线 —— 自主 Orion AI 智能体',
      painPoints: [
        '质量预警触发的下游动作涉及供应商、产线、客户——协调零散。',
        '每个角色用自己的工具/渠道;预警不能自动转化为可执行任务。',
      ],
      solution: [
        'Orion AI 智能体自动调用"质量追溯"、"任务派发"等 Onto-Skills。',
        '基于预警单生成改进报告。',
        '差异化任务派发到:供应商负责人、产线负责人、客户助手(部署在团队沟通平台上的智能体)。',
        '端到端:问题发现 → 追溯报告 → 协同派发——全自动。',
      ],
      metrics: [
        { label: '问题发现 → 任务派发', before: '人工扇出', after: '自动 · 角色感知' },
        { label: '协同闭环', before: '多工具、多渠道', after: '单一本体驱动流程' },
      ],
      insight:
        '当 ontology 天然说"供应商 · 产线 · 客户",Agent 只需要做路由——不再需要集成胶水。',
    },
    {
      id: 'ceramic-bearing-design',
      industryTag: '制造业',
      functionTag: '研发 · 设计 → 生产闭环',
      paradigmTags: ['Goal-driven', 'Change-propagation', 'Multi-agent'],
      customer: '工业轴承设计',
      title: '陶瓷复合材料轴承 —— 一条链贯通设计与采购',
      painPoints: [
        '轴承研发输出(材料配方 + 结构参数 + 性能 + 适配场景)历史上需要人工分别交给 PDM、ERP、MES。',
        '物料采购由人工"想起来要采"触发——慢且易遗忘。',
        '约束验证(耐温、疲劳寿命)晚发现、贵——有时事后才知道。',
      ],
      solution: [
        '本体定义"设计成果":实体(材料 / 结构 / 性能 / 场景)+ 关联规则。',
        'Agent 自动拆解设计方案:SOLIDWORKS 参数化文件(如陶瓷颗粒占比 35%、保持架镂空率 20%)、BOM 清单(由"轴承类型 - 物料编码"映射生成)、约束清单(耐温 ≥ 180℃、疲劳寿命 > 100 万次)。',
        '跨系统传播:图纸 + 版本说明 → PDM;BOM → ERP;工艺调整单 → MES;采购订单(如向 A 企业下达 3 吨纳米氮化硅)→ 采购系统。',
        '阈值越界的人在环中:陶瓷材料成本超预算 10% → 触发人工审批,系统推"成本-性能权衡分析报告"。',
      ],
      metrics: [
        { label: '设计 → 采购交接', before: '人工 / 串行', after: '自动传播' },
        { label: '材料约束验证', before: '晚 / 贵', after: '设计阶段' },
        { label: '成本超支审批', before: '被动', after: '自动路由 + 决策报告' },
      ],
      insight:
        '设计和生产不再是两个孤岛——它们共用一份知识结构。',
    },
    {
      id: 'city-service-hotline',
      industryTag: '政府',
      functionTag: '客户服务 · 风险 · 数据洞察',
      paradigmTags: ['Question-grounded', 'Document-driven', 'Event-driven', 'Continuous-audit'],
      customer: '某主要城市政府服务热线(100 个坐席)',
      title: '市级服务热线 —— 每个维度都更智能',
      painPoints: [
        '坐席知识储备差异大,面对市民多样化诉求,工单分拨易偏差。',
        '排队等待长,单次服务时长高,直接办复率仅 54.25%。',
        '报告由分析员人工撰写——以天计。',
        '8000+ 通日呼叫的风险感知薄弱,潜在隐患难浮现。',
      ],
      solution: [
        '坐席端实时匹配:大模型在通话过程中自动检索相似历史最佳实践。',
        '情绪识别 + 工单自动摘要 + 自动分类 + 受理单位推荐 + 自动回复 + 一键派单。',
        '智能问数:分析员自然语言提问,获得数据 + 文字洞察 + 图表展示。',
        '城市侧每日风险态势自动报告:高风险事件、热点事件、小时趋势、工作要点——基于模板 + LLM 分钟级生成。',
      ],
      metrics: [
        { label: '服务坐席智慧助手覆盖', before: '0', after: '100 个全配' },
        { label: '排队等待时长', before: '17 秒', after: '11 秒' },
        { label: '单次服务时长', before: '基线', after: '缩短 30%+' },
        { label: '直接办复率', before: '54.25%', after: '77.6%' },
        { label: '报告撰写', before: '数天', after: '几分钟' },
      ],
      insight:
        '三层(坐席 · 分析员 · 城市运营)用一份 ontology——同一份数据同时支撑服务质量、决策洞察和风险感知。',
    },
    {
      id: 'asset-management-soe',
      industryTag: '政府 · 不动产',
      functionTag: '资产管理 · 选址 · 闲置盘活',
      paradigmTags: ['Question-grounded', 'Document-driven', 'Risk-scoring'],
      customer: '某大型公共部门企业(管理大量土地资产)',
      title: '土地资产盘活 —— 唤醒沉睡的资产',
      painPoints: [
        '资产数据散在资产管理 / 财务 / 租赁信息等多个系统,缺乏动态更新的统一视图。',
        '跨区域、跨类型(商办 / 工业 / 住宅)资产利用率对比分析困难。',
        '无法快速定位经营异常(闲置率高)企业,无法诊断原因(产权纠纷 / 区位劣势)。',
        '缺乏智能选址工具匹配项目对面积 / 交通 / 配套等的复合要求。',
        '缺乏各省市土地集约利用案例库,政策工具(容积率奖励、混合用地)应用不充分。',
      ],
      solution: [
        '资产信息管理系统:整合资产 / 租赁 / 合同 / 财务为一个动态视图;跨区域跨类型对比分析浮现低效资产和风险点。',
        '智能问答助手:自然语言查询资产信息、盘活策略、选址建议;复杂问题通过追问、引导、关联多个数据源给出深入解答。',
        '集成各省市案例库——可快速查询类似项目的成功经验。',
        '智能报告生成:一键生成月度资产动态、闲置分析、盘活效果评估、选址建议——支持地图联动可视化。',
      ],
      metrics: [
        { label: '数据整合', before: '人工跨系统', after: '统一动态视图' },
        { label: '低效资产诊断', before: '慢 / 局部', after: '自动 + 归因明确' },
        { label: '选址支持', before: '无工具', after: '自然语言推荐' },
        { label: '管理模式', before: '经验驱动', after: '数据驱动' },
      ],
      insight:
        '闲置资产不是不动产问题,是知识图谱问题——把已有的连起来,下一步决策就清晰了。',
    },
    {
      id: 'consumer-sales-production-sync',
      industryTag: '制造 · 消费',
      functionTag: '销售预测 · 产销协同 · 库存',
      paradigmTags: ['What-if', 'Goal-driven', 'Change-propagation'],
      customer: '某头部消费品制造企业(SKU 庞大,渠道层级复杂)',
      title: '产销协同 —— 从博弈到计算',
      painPoints: [
        '销售预测依赖经验;数据散在多系统;旺季预测偏差尤其明显。',
        '生产计划"慢半拍"——要么供应不足,要么库存占用资金。',
        '决策层手握大量报表,却很难获得对市场趋势 / 产能匹配 / 策略调整的清晰判断。',
      ],
      solution: [
        '复合架构:Ontology 业务语义体系 + 大模型 + 多智能体协同。',
        'Orion AI 销售及生产预测智能体:整合历史销售订单 + 行业趋势 + 上下游动态 + 政策导向 → 统一语义数据视图;每个输入可追溯。',
        '多粒度预测(产品级 / 系列级 / 渠道级)结合设备 / 人力 / 物料 / 在制品约束;自动生成柔性排产规划。',
        '输出丰富:图表 / 表格 / 多格式导出 / 推理链路可视化——决策层不仅看到答案,还看到答案怎么来的。',
      ],
      metrics: [
        { label: '销售预测准确率', before: '基线', after: '整体提升约 20%+' },
        { label: '库存占用率', before: '基线', after: '平均下降约 15%' },
        { label: '市场响应周期', before: '周级', after: '小时级动态调整' },
        { label: '跨部门沟通', before: '争论数字', after: '围绕策略共识推进' },
      ],
      insight:
        '产销协同从"季度博弈"变成"日常计算"——而且推理链路对每个决策者可见。',
    },
    {
      id: 'consumer-customer-service',
      industryTag: '汽车 · 零售',
      functionTag: '客户服务 · 业务洞察',
      paradigmTags: ['Question-grounded', 'Document-driven', 'Change-propagation'],
      customer: '国内某新势力电动自行车企业(20+ 系列、数十种车型)',
      title: '电动自行车智能客服 —— 服务数据反哺业务决策',
      painPoints: [
        '多个产品系列和型号数据高度相似——系统检索容易混淆。',
        '用户咨询口语化:"这款车续航怎么样?"、"和之前那个比差在哪?"',
        '结构化(车辆参数)与非结构化(产品手册)缺乏跨模态整合——回答不精准。',
        '客服系统的数据只用于一次性应答——销售和管理层不能用它做趋势判断。',
      ],
      solution: [
        '结构化 + 非结构化知识库:结构化走 Embedding + 索引 + 检索;非结构化(手册 / 故障指南)通过 OCR + 图表识别 + 版面还原入库。',
        '知识图谱 + 大模型,产品层次(类别 / 系列 / 型号)清晰——大模型在语义约束内工作,不再"猜问题、乱联想"。',
        '客服与在线业务打通:Agent 不止知识问答,还能调用车辆报修、备件购买等在线业务程序。',
        '服务数据反哺业务:销售可以问"哪些车型咨询热度上升却没转化为销量?"——以自然语言获得答案。',
      ],
      metrics: [
        { label: '提问理解', before: '型号间易混淆', after: '通过层次消歧' },
        { label: '服务 → 业务回路', before: '止步于一次应答', after: '销售 / 管理可查同一份数据' },
        { label: '跨模态数据', before: '孤岛', after: '统一' },
        { label: '一个洞察例子', before: '—', after: '识别"续航参数被误解 → 转化偏低";调整说明后落差收敛' },
      ],
      insight:
        '客户服务变成业务的传感器——每一次互动产生的信号,销售和管理后面都能查得到。',
    },
    {
      id: 'soe-project-management',
      industryTag: '政府',
      functionTag: '项目管理 · 数据问数',
      paradigmTags: ['Question-grounded', 'Explainability'],
      customer: '某大型企业集团',
      title: '数十万项目的 AI 对话式问数',
      painPoints: [
        '百余项基本属性 + 千余个项目评价指标,覆盖数十万个项目。',
        '传统数据查询依赖技术人员开发的报表 / 驾驶舱——不灵活。',
        '查询结果只是数字,缺业务解释和因果说明。',
        '用户问题模糊:项目名称用昵称或错别字;有些指标需要实时计算;角色含糊("某项目负责人是谁?"——运维经理 / 采购经理 / 工程经理 / 实施经理?)。',
      ],
      solution: [
        'KG 驱动的行业术语 / 数据 / 业务体系知识结构。',
        '大模型 + GraphRAG 图增强检索:超强意图理解 + 业务对齐 + 查询泛化 + 结果收敛。',
        '主动澄清意图——系统在回答之前先问清楚。',
        '多形态结果(文字 / 图表 / 图谱) + 行业上下文解读 = "问答即洞察"。',
      ],
      metrics: [
        { label: '覆盖', before: '静态报表', after: '数十万项目' },
        { label: '指标', before: '固定', after: '千余个,可自然语言' },
        { label: '提问路径', before: '报表 + 驾驶舱', after: '自助自然语言问数' },
        { label: '可复用', before: '仅项目', after: '资产 / 合同 / 销售 / 人力 / 财务' },
      ],
      insight:
        '当本体把组织的"语言"建模出来,业务人员不再排队等数据工程师——他们直接和数据对话。',
    },
    {
      id: 'research-direction-discovery',
      industryTag: '研发 · 工业研究院',
      functionTag: '研发战略 · 科技情报',
      paradigmTags: ['Document-driven', 'Goal-driven', 'Continuous-audit'],
      customer: '某工业研究院(承担关键科研任务)',
      title: '画出技术地图 · 找出卡脖子',
      painPoints: [
        '技术产业链脉络认识不完整,难以系统梳理和关联全产业链。',
        '内外部情报信息掌握不足;缺乏及时、全面、高效的科技情报管理。',
        '项目定位属性不清晰,难以准确判断核心价值和市场定位。',
        '研发预算容易投到饱和或非关键方向;有错失前瞻性机会的风险。',
      ],
      solution: [
        '技术产品谱系管理:Orion AI 知识图谱建模全产业链——工序、工艺、技术产品、研发方向系统梳理 + 分层 + 关联。',
        '科技情报知识库:统一收录国内外行业报告、研究成果、期刊、论文、资讯、竞争对手动态;自然语言检索;大模型从海量信息中提取价值;KG 自动关联工序 / 工艺 / 技术产品 / 研发方向 / 项目情况。',
        '研发方向动态评估:从海量内外部情报提取关键指标支撑决策;评估走向科学化、系统化。',
        '自动推送高价值情报给相关项目成员——趋势、前沿技术、机会自动到位。',
      ],
      metrics: [
        { label: '科技文献处理', before: '—', after: '15 万余篇' },
        { label: '资讯文章处理', before: '—', after: '10 万余篇' },
        { label: '推荐关键标签', before: '—', after: '1,000 余个' },
        { label: '引领未来技术识别', before: '—', after: '142 项' },
        { label: '长板技术识别', before: '—', after: '225 项' },
        { label: '卡脖子技术识别', before: '—', after: '19 项' },
      ],
      insight:
        '战略决策从"专家直觉"走向"证据支撑的评估"——同一个引擎随后继续盯着,看领域如何演化。',
    },
  ],
  playbookCta: {
    tag: '操作手册',
    title: '怎么做成自己的',
    body: '这些案例其实只是少数几个模式的不同表达——从哪开始、怎么建本体、怎么控制行为。这些都在 Playbook 里,11 段,没有水分。',
    cta: '进入 Playbook →',
  },
}
